import React, { useCallback, useEffect, useState } from "react";
import { api, fmtDate, fmtMoney, downloadWithToast } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import ProformaEditorSheet from "@/components/sales/ProformaEditorSheet";
import ProformaSettingsDialog from "@/components/sales/ProformaSettingsDialog";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, Trash, FilePdf, ArrowsClockwise, MagnifyingGlass, PencilLine,
  PaperPlaneTilt, CheckCircle, XCircle, Bank,
} from "@phosphor-icons/react";

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700",
  sent: "bg-blue-50 text-blue-700",
  paid: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-700",
};

export default function ProformaInvoices() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || user?.role === "manager";
  const canEditSettings = canDelete;

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/proforma-invoices", { params: { q, status: statusFilter, limit: 200 } });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }, [q, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/proforma-invoices/${id}/status`, { status });
      toast.success(`Status → ${status}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const remove = async (id, no) => {
    if (!window.confirm(`Delete Proforma Invoice ${no}? This cannot be undone.`)) return;
    try {
      await api.delete(`/proforma-invoices/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-4 p-4" data-testid="proforma-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold font-display text-slate-900 tracking-tight">Proforma Invoices</h1>
          <div className="text-[11.5px] text-slate-500">{total} total · VRV equipment at agreed rate-contract prices</div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" className="h-8" onClick={load} data-testid="pi-refresh">
            <ArrowsClockwise size={12} className="mr-1" /> Refresh
          </Button>
          {canEditSettings && (
            <Button variant="outline" size="sm" className="h-8" onClick={() => setSettingsOpen(true)} data-testid="pi-settings-btn">
              <Bank size={13} className="mr-1" /> Bank & Settings
            </Button>
          )}
          <Button onClick={() => setCreating(true)} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="new-pi-btn">
            <Plus size={13} className="mr-1" /> New Proforma Invoice
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm px-3 py-2 flex-1 min-w-64">
          <MagnifyingGlass size={14} className="text-slate-400" />
          <Input placeholder="Search PI #, buyer, subject…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 text-[12px] w-full border-0 shadow-none focus-visible:ring-0 px-0" data-testid="pi-search" />
        </div>
        {["", "draft", "sent", "paid", "cancelled"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-sm text-[11px] font-semibold border transition-colors ${statusFilter === s ? "border-[#EA580C] bg-orange-50 text-[#EA580C]" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}
            data-testid={`pi-filter-${s || "all"}`}
          >
            {s ? s.toUpperCase() : "ALL"}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="text-[11.5px]">
              <TableHeader>
                <TableRow className="bg-[#0F172A]">
                  <TableHead className="text-white/90 w-32">PI #</TableHead>
                  <TableHead className="text-white/90 w-24">Date</TableHead>
                  <TableHead className="text-white/90 w-56">Buyer</TableHead>
                  <TableHead className="text-white/90 w-20 text-center">Tax</TableHead>
                  <TableHead className="text-white/90 text-right w-32">Grand Total</TableHead>
                  <TableHead className="text-white/90 text-center w-24">Status</TableHead>
                  <TableHead className="text-white/90 w-60">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">
                    {busy ? "Loading…" : "No proforma invoices yet. Create your first one!"}
                  </TableCell></TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`pi-row-${r.pi_no}`}>
                    <TableCell className="font-mono font-semibold text-slate-900">{r.pi_no}</TableCell>
                    <TableCell className="text-[11px]">{fmtDate(r.date)}</TableCell>
                    <TableCell className="text-[11.5px] text-slate-800">{r.buyer?.name || "—"}</TableCell>
                    <TableCell className="text-center text-[10px] font-semibold uppercase text-slate-500">{r.tax_mode === "inter" ? "IGST" : "CGST+SGST"}</TableCell>
                    <TableCell className="text-right font-mono-num font-bold">{fmtMoney(r.grand_total)}</TableCell>
                    <TableCell className="text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-widest ${STATUS_COLORS[r.status] || "bg-slate-100"}`}>{r.status}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" onClick={() => setEditing(r.id)} data-testid={`pi-open-${r.id}`}>
                          <PencilLine size={11} className="mr-0.5" /> Open
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" title="Download PDF" onClick={() => downloadWithToast(`/proforma-invoices/${r.id}/pdf`, `${r.pi_no.replace(/\//g, "-")}.pdf`).catch(() => {})} data-testid={`pi-pdf-${r.id}`}>
                          <FilePdf size={11} />
                        </Button>
                        {r.status === "draft" && (
                          <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-blue-600 border-blue-200 hover:bg-blue-50" title="Mark sent" onClick={() => setStatus(r.id, "sent")} data-testid={`pi-sent-${r.id}`}>
                            <PaperPlaneTilt size={11} />
                          </Button>
                        )}
                        {r.status === "sent" && (
                          <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-emerald-700 border-emerald-200 hover:bg-emerald-50" title="Mark paid" onClick={() => setStatus(r.id, "paid")} data-testid={`pi-paid-${r.id}`}>
                            <CheckCircle size={11} />
                          </Button>
                        )}
                        {r.status !== "cancelled" && r.status !== "paid" && (
                          <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-amber-700 border-amber-200 hover:bg-amber-50" title="Cancel" onClick={() => setStatus(r.id, "cancelled")} data-testid={`pi-cancel-${r.id}`}>
                            <XCircle size={11} />
                          </Button>
                        )}
                        {canDelete && (
                          <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-red-600 border-red-200 hover:bg-red-50" onClick={() => remove(r.id, r.pi_no)} data-testid={`pi-delete-${r.id}`}>
                            <Trash size={11} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {creating && <ProformaEditorSheet onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <ProformaEditorSheet piId={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <ProformaSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}
